import { type InfiniteData, type QueryKey, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { identity, isEmpty } from 'lodash';
import { useCallback } from 'react';

import {
  type AssetSearchFilters,
  type Board,
  type ClipAndBoardListItem,
  type Library,
  type ListResponse,
} from '@air/api/types';

import { isLibraryBoardsListKey } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { isPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { PRIVATE_BOARDS_LIST_KEY } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { GALLERY_BOARDS, GALLERY_MIXED_DATA } from '~/constants/react-query-keys';
import { isPrivateGalleryViewItemsQueryKey } from '~/swr-hooks/gallery/galleryView/usePrivateGalleryViewItems';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import { getUpdatedBoard } from '~/utils/BoardUtils';
import { useUpdateBoardStats } from '~/utils/mutateUtils/BoardStats';
import { getOrderedBoards } from '~/utils/mutateUtils/getOrderedBoards';
import { getRearrangedBoards, getUpdatedBoards, type UpdatedBoardBase } from '~/utils/mutateUtils/mutators';
import { useUpdateKanbanData } from '~/utils/mutateUtils/useUpdateKanbanData';
import { getBoardIdFromPath } from '~/utils/PathUtils';

const getQueryKey = ({
  mainKey,
  parentBoardId,
  libraryId,
}: {
  mainKey: string;
  parentBoardId?: Board['id'] | '*';
  libraryId?: Library['id'];
}) => {
  // https://linear.app/air/issue/ENG-127/update-getquerykey-in-galleryboard-utils-to-not-use-any
  // biome-ignore lint/suspicious/noExplicitAny: <https://linear.app/air/issue/ENG-127/update-getquerykey-in-galleryboard-utils-to-not-use-any>
  const keyToUpdate: any[] = [mainKey];

  const filters: AssetSearchFilters = {};

  if (parentBoardId && parentBoardId !== '*') {
    filters.board = { is: parentBoardId };
  }

  if (libraryId) {
    filters.library = { is: libraryId };
  }

  if (!parentBoardId && !libraryId) {
    filters.onlyInLibraries = { is: false };
  }

  if (!isEmpty(filters)) {
    keyToUpdate.push({ filters });
  }

  return keyToUpdate;
};

const isGalleryKey = ({
  queryKey,
  parentBoardId,
  libraryId,
}: {
  queryKey: QueryKey;
  parentBoardId?: string;
  libraryId?: string;
}) => {
  if (!isPrivateGalleryViewItemsQueryKey(queryKey)) {
    return false;
  }

  if (parentBoardId === '*') {
    return true;
  }

  if (!!parentBoardId && queryKey[1]?.filters?.board?.is === parentBoardId) {
    return true;
  }

  if (!!libraryId && queryKey[1]?.filters?.library?.is === libraryId) {
    return true;
  }

  // The General library is not a real library entity, so boards in it carry no library id and no
  // parent board. Its view is keyed by the "not in any library" filter, matching getQueryKey above.
  if (!parentBoardId && !libraryId) {
    return queryKey[1]?.filters?.onlyInLibraries?.is === false;
  }

  return false;
};

const useMutateBoardsInAllViews = () => {
  const queryClient = useQueryClient();

  const updateCache = useCallback(
    <T>({
      mainKey,
      parentBoardId,
      updateFn,
      libraryId,
    }: {
      mainKey: string;
      parentBoardId?: string;
      libraryId?: string;
      updateFn: (data?: T[]) => T[] | undefined;
    }) => {
      queryClient.setQueriesData<InfiniteData<T>>(
        { queryKey: getQueryKey({ parentBoardId, libraryId, mainKey }) },
        (data) => {
          return data
            ? {
                pages: updateFn(data?.pages) || [],
                pageParams: data?.pageParams,
              }
            : undefined;
        },
      );
    },
    [queryClient],
  );

  const mutateGalleryData = useCallback(
    ({
      updateFn,
      libraryId,
      parentBoardId,
    }: {
      parentBoardId?: string;
      updateFn: (data?: ListResponse<Board>[]) => ListResponse<Board>[] | undefined;
      libraryId?: string;
    }) => {
      const queries = queryClient.getQueriesData({
        predicate: (query) => {
          return isGalleryKey({ queryKey: query.queryKey, parentBoardId, libraryId });
        },
      });

      queries.forEach(([queryKey]) => {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>>>({ queryKey }, (data) => {
          return data
            ? {
                pages: updateFn(data?.pages) || [],
                pageParams: data?.pageParams,
              }
            : undefined;
        });
      });
    },
    [queryClient],
  );

  const mutateTableData = useCallback(
    ({
      parentBoardId,
      libraryId,
      updateFn,
    }: {
      parentBoardId?: string;
      libraryId?: string;
      updateFn: (data?: ListResponse<ClipAndBoardListItem>[]) => ListResponse<ClipAndBoardListItem>[] | undefined;
    }) => updateCache({ mainKey: GALLERY_MIXED_DATA, parentBoardId, updateFn, libraryId }),
    [updateCache],
  );

  return {
    mutateGalleryData,
    mutateTableData,
  };
};

/**
 * Use this hook to refresh boards (refetch) in all views (gallery, table etc)
 */
export const useRefreshBoardsInAllViews = () => {
  const queryClient = useQueryClient();

  /**
   * Use this method to update mixed data in SWR cache
   * @param parentBoardId parent board id, or `*` to refresh every board list
   */
  const refreshBoardsInAllViews = useCallback(
    (parentBoardId: Board['id'] | '*') => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: getQueryKey({ mainKey: GALLERY_BOARDS, parentBoardId }) }),
        queryClient.invalidateQueries({ queryKey: getQueryKey({ mainKey: GALLERY_MIXED_DATA, parentBoardId }) }),
        queryClient.invalidateQueries({ queryKey: [PRIVATE_BOARDS_LIST_KEY] }),
      ]);
    },
    [queryClient],
  );

  return {
    refreshBoardsInAllViews,
  };
};

/**
 * Clear generated board thumbnails while asset indexes catch up after a bulk move.
 * Explicit cover images are left untouched.
 */
export const useClearBoardThumbnailsInAllViews = () => {
  const queryClient = useQueryClient();

  const clearBoardThumbnailsInAllViews = useCallback(
    (boardIds: Board['id'][]) => {
      if (boardIds.length === 0) {
        return;
      }

      const boardIdsSet = new Set(boardIds);
      const clearThumbnails = (board: Board) => (boardIdsSet.has(board.id) ? { ...board, thumbnails: [] } : board);

      queryClient.setQueriesData<InfiniteData<ListResponse<Board>>>({ queryKey: [GALLERY_BOARDS] }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                data: page.data.map(clearThumbnails),
              })),
            }
          : undefined,
      );
      queryClient.setQueriesData<InfiniteData<ListResponse<ClipAndBoardListItem>>>(
        { queryKey: [GALLERY_MIXED_DATA] },
        (data) =>
          data
            ? {
                ...data,
                pages: data.pages.map((page) => ({
                  ...page,
                  data: page.data.map((item) =>
                    item.type === 'board' ? { ...item, data: clearThumbnails(item.data as Board) } : item,
                  ),
                })),
              }
            : undefined,
      );
      queryClient.setQueriesData<InfiniteData<ListResponse<Board>>>({ queryKey: [PRIVATE_BOARDS_LIST_KEY] }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                data: page.data.map(clearThumbnails),
              })),
            }
          : undefined,
      );
      queryClient.setQueriesData<Board>(
        {
          predicate: ({ queryKey }) =>
            isPrivateBoardKey(queryKey) && !!queryKey[1].boardId && boardIdsSet.has(queryKey[1].boardId),
        },
        (board) => (board ? clearThumbnails(board) : undefined),
      );
    },
    [queryClient],
  );

  return { clearBoardThumbnailsInAllViews };
};

/**
 * Use this hook to update single board in all views (gallery, table etc)
 */
export const useUpdateBoardInAllViews = () => {
  const { mutateTableData } = useMutateBoardsInAllViews();
  const queryClient = useQueryClient();

  const updateBoardInAllViews = useCallback(
    (updatedBoard: UpdatedBoardBase) => {
      const parentId = updatedBoard.parentId || '';

      return Promise.all([
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          {
            queryKey: getQueryKey({
              parentBoardId: parentId,
              mainKey: GALLERY_BOARDS,
              libraryId: updatedBoard.library?.id,
            }),
          },
          getUpdatedBoards(updatedBoard),
        ),
        mutateTableData({
          parentBoardId: parentId,
          libraryId: updatedBoard.library?.id,
          updateFn: (data) =>
            data?.reduce((acc, curr) => {
              acc.push({
                ...curr,
                data: curr.data.map((item) => {
                  if (item.type === 'board') {
                    const board = item.data as Board;

                    return {
                      ...item,
                      data:
                        board.id === updatedBoard.id
                          ? getUpdatedBoard({ newBoard: updatedBoard, oldBoard: board })
                          : board,
                    };
                  }

                  return item;
                }),
              });

              return acc;
            }, [] as ListResponse<ClipAndBoardListItem>[]),
        }),
      ]);
    },
    [mutateTableData, queryClient],
  );

  return {
    updateBoardInAllViews,
  };
};

/**
 * Use this hook to remove board from all views (gallery, table etc)
 */
export const useRemoveBoardsFromAllViews = () => {
  const { mutateTableData, mutateGalleryData } = useMutateBoardsInAllViews();
  const { updateBoardStats } = useUpdateBoardStats();
  const { removeKanbanItems } = useUpdateKanbanData();

  const removeBoardsFromAllViews = useCallback(
    ({
      boardsIdsToRemove,
      parentBoardId,
      libraryId,
    }: {
      /*
       * Pass '*' to remove board from all views
       */
      parentBoardId?: string | '*';
      libraryId?: string;
      boardsIdsToRemove: string[];
    }) => {
      const currentBoardId = getBoardIdFromPath(window.location.pathname);

      if (parentBoardId) {
        updateBoardStats(parentBoardId);
      }
      if (currentBoardId && currentBoardId !== parentBoardId) {
        updateBoardStats(currentBoardId);
      }
      void Promise.all([
        removeKanbanItems({ boardIds: boardsIdsToRemove }),
        mutateGalleryData({
          parentBoardId,
          libraryId,
          updateFn: (data) => {
            let totalRemoved = 0;

            const newData = data?.reduce((acc, curr) => {
              const newBoards = curr.data.filter((board) => !boardsIdsToRemove.includes(board.id));
              totalRemoved += curr.data.length - newBoards.length;

              acc.push({
                ...curr,
                data: curr.data.filter((board) => !boardsIdsToRemove.includes(board.id)),
              });
              return acc;
            }, [] as ListResponse<Board>[]);

            return produce(newData, (draft) => {
              draft?.forEach((data) => {
                data.total = data.total - totalRemoved;
              });
            });
          },
        }),
        mutateTableData({
          parentBoardId,
          libraryId,
          updateFn: (data) => {
            let totalRemoved = 0;

            const newData = data?.reduce((acc, curr) => {
              const newBoards = curr.data.filter(
                ({ data, type }) => type !== 'board' || !boardsIdsToRemove.includes((data as Board).id),
              );
              totalRemoved += curr.data.length - newBoards.length;

              acc.push({
                ...curr,
                data: newBoards,
              });
              return acc;
            }, [] as ListResponse<ClipAndBoardListItem>[]);

            return produce(newData, (draft) => {
              draft?.forEach((data) => {
                data.total = data.total - totalRemoved;
              });
            });
          },
        }),
      ]);
    },
    [mutateGalleryData, mutateTableData, removeKanbanItems, updateBoardStats],
  );

  return {
    removeBoardsFromAllViews,
  };
};

/**
 * Use this hook to update rearranged boards in gallery
 */
export const useRearrangeBoardsInGallery = () => {
  const queryClient = useQueryClient();
  /**
   * Use this method to rearrange boards in gallery
   * @param parentBoardId parent board id
   * @param rearrangedBoards all existing boards in new order
   * @param insertAtIndex absolute page-0 index where the moved boards should
   * land; omit to fall back to append-at-end (used for non-drag callers).
   */
  const rearrangeBoardsInGallery = useCallback(
    ({
      parentBoardId,
      libraryId,
      rearrangedBoards,
      insertAtIndex,
    }: {
      parentBoardId?: string;
      rearrangedBoards: Board[];
      libraryId?: string;
      insertAtIndex?: number;
    }) => {
      const queries = queryClient.getQueriesData({
        predicate: (query) => {
          return isGalleryKey({ queryKey: query.queryKey, parentBoardId, libraryId });
        },
      });

      queries.forEach(([queryKey]) => {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          { queryKey },
          getRearrangedBoards(rearrangedBoards, insertAtIndex),
        );
      });

      // Also update LIBRARY_BOARDS_LIST cache if these boards are in a library
      if (libraryId) {
        const libraryBoardQueries = queryClient.getQueriesData({
          predicate: (query) => {
            return isLibraryBoardsListKey(query.queryKey) && query.queryKey[1]?.libraryId === libraryId;
          },
        });

        libraryBoardQueries.forEach(([queryKey]) => {
          queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
            { queryKey },
            getRearrangedBoards(rearrangedBoards, insertAtIndex),
          );
        });
      }
    },
    [queryClient],
  );

  return {
    rearrangeBoardsInGallery,
  };
};

/**
 * Use this hook to add board to all views (gallery, table etc)
 */
export const useAddBoardsToAllViews = () => {
  const queryClient = useQueryClient();
  const { mutateGalleryData, mutateTableData } = useMutateBoardsInAllViews();
  const { addItemsToKanbanColumn } = useUpdateKanbanData();
  const { updateBoardStats } = useUpdateBoardStats();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  /**
   * Use this method to add boards to gallery
   * @param parentBoardId parent board id
   * @param boardsToAdd new boards
   */
  const addBoardsToAllViews = useCallback(
    async ({
      parentBoardId,
      libraryId,
      boardsToAdd,
    }: {
      parentBoardId?: string;
      libraryId?: string;
      boardsToAdd: Board[];
    }) => {
      const currentBoardId = getBoardIdFromPath(window.location.pathname);

      if (parentBoardId && parentBoardId === currentBoardId) {
        addItemsToKanbanColumn({ boards: boardsToAdd });
        updateBoardStats(parentBoardId);
      }

      // Get the appropriate sort for this context
      const sortValue = getSubNavSortValue(libraryId);
      const boardSort = libraryId ? sortValue?.libraryBoardSorts?.[0] : sortValue?.boardSort;

      // Cancel any in-flight fetches for these views so a stale response
      // can't overwrite the optimistic add once it lands.
      await Promise.all([
        queryClient.cancelQueries({
          predicate: (query) => isGalleryKey({ queryKey: query.queryKey, parentBoardId, libraryId }),
        }),
        queryClient.cancelQueries({
          queryKey: getQueryKey({ mainKey: GALLERY_MIXED_DATA, parentBoardId, libraryId }),
        }),
      ]);

      return Promise.all([
        mutateGalleryData({
          parentBoardId,
          libraryId,
          updateFn: (data) => {
            if (data) {
              return produce(data, (draft) => {
                const firstData = draft[0];
                const allData = [...firstData.data, ...boardsToAdd];

                // Sort all boards according to current sort settings
                if (boardSort) {
                  const { orderedBoards } = getOrderedBoards(allData, boardSort);
                  firstData.data = orderedBoards;
                } else {
                  firstData.data = allData;
                }

                firstData.total = firstData.total + boardsToAdd.length;
              });
            } else {
              return [
                {
                  data: [...boardsToAdd],
                  total: boardsToAdd.length,
                  pagination: {
                    cursor: null,
                    hasMore: false,
                  },
                },
              ];
            }
          },
        }),
        mutateTableData({
          parentBoardId,
          libraryId,
          updateFn: (data) => {
            const boardsData: ClipAndBoardListItem[] = boardsToAdd.map((board) => ({
              type: 'board',
              data: board,
              id: board.id,
            }));

            if (data) {
              return produce(data, (draft) => {
                const firstData = draft[0];

                if (libraryId && boardSort) {
                  const existingBoardData = firstData.data.filter(
                    (item): item is { type: 'board'; data: Board; id: string } => item.type === 'board',
                  );
                  const allBoards = [...existingBoardData.map((item) => item.data), ...boardsToAdd];

                  // Sort all boards according to current sort settings
                  let sortedBoards: Board[];

                  if (boardSort) {
                    const { orderedBoards } = getOrderedBoards(allBoards, boardSort);
                    sortedBoards = orderedBoards;
                  } else {
                    sortedBoards = allBoards;
                  }

                  // Convert back to ClipAndBoardListItem format
                  const sortedBoardData: ClipAndBoardListItem[] = sortedBoards.map((board) => ({
                    type: 'board',
                    data: board,
                    id: board.id,
                  }));

                  firstData.data = sortedBoardData;
                } else {
                  firstData.data = [...boardsData, ...firstData.data];
                }

                firstData.total = firstData.total + boardsToAdd.length;
              });
            } else {
              return [
                {
                  data: boardsData,
                  total: boardsToAdd.length,
                  pagination: {
                    cursor: null,
                    hasMore: false,
                  },
                },
              ];
            }
          },
        }),
      ]);
    },
    [queryClient, mutateGalleryData, mutateTableData, addItemsToKanbanColumn, updateBoardStats, getSubNavSortValue],
  );

  return {
    addBoardsToAllViews,
  };
};

type UpdateBoardsInAllViewsParams = {
  parentBoardId?: string;
  libraryId?: string;
  boardIds: string[];
  updateFn?: (existingBoard: Board) => Board;
};

/**
 * Use this hook to update boards in all views (gallery, table etc)
 */
export const useUpdateBoardsInAllViews = () => {
  const { mutateTableData, mutateGalleryData } = useMutateBoardsInAllViews();
  const { updateKanbanData } = useUpdateKanbanData();

  const updateBoardsInAllViews = useCallback(
    ({ boardIds, parentBoardId = '', libraryId, updateFn = identity }: UpdateBoardsInAllViewsParams) => {
      void Promise.all([
        updateKanbanData({
          boardIds,
          updateBoardFn: updateFn,
        }),
        mutateGalleryData({
          parentBoardId,
          libraryId,
          updateFn: (data) => {
            const idsSet = new Set(boardIds);

            if (data) {
              return data.map((response) =>
                produce(response, (nextResponse) => {
                  nextResponse.data = nextResponse.data.map((board) => {
                    return idsSet.has(board.id) ? updateFn(board) : board;
                  });
                }),
              );
            }
          },
        }),
        mutateTableData({
          parentBoardId,
          libraryId,
          updateFn: (data) => {
            const idsSet = new Set(boardIds);

            if (data) {
              // NOTE: This may need to match the mutateGalleryData use of .map instead of .forEach
              return data.map((response) =>
                produce(response, (nextResponse) => {
                  nextResponse.data.forEach((item) => {
                    if (item.type === 'board' && idsSet.has((item.data as Board).id)) {
                      item.data = updateFn(item.data as Board);
                    }
                  });
                }),
              );
            }
          },
        }),
      ]);
    },
    [updateKanbanData, mutateGalleryData, mutateTableData],
  );

  return {
    updateBoardsInAllViews,
  };
};
